import classNames from "classnames";
import React from "react";
import KuikaDrawnSvg from "./kuika-drawn-svg";
import "./kuika-svg.scss";

export interface KuikaSvgProps {
  name: string;
  type?:
    | "common"
    | "datasource-icons"
    | "sidebar-icons"
    | "styling-panel-icons"
    | "action-icons"
    | "arrows"
    | "topbar-icons"
    | "flags"
    | "workflow-icons"
    | "screen-templates"
    | "screen-templates-icons"
    | "agent-icons"
    | "ai-agent-tools";
  width?: number;
  height?: number;
  color?: string;
  style?: React.CSSProperties;
  onClick?: (e?: any) => void;
  pointer?: boolean;
  className?: string;
  disabled?: boolean;
  useOriginalColors?: boolean;
  /**
   * Trace the icon's own strokes on once, when it mounts, instead of painting it flat. Decorative
   * only (it renders inline, aria-hidden); falls back to the flat icon if the file cannot be drawn.
   */
  draw?: boolean;
  [key: string]: any;
}

const KuikaSvg: React.FC<KuikaSvgProps> = ({
  name,
  type = "common",
  width,
  height,
  color,
  style,
  onClick,
  pointer = false,
  className,
  disabled = false,
  useOriginalColors = false,
  draw = false,
  ...rest
}) => {
  const _type = type === "screen-templates-icons" ? "screen-templates/icons" : type;
  const imageSrc = `/images/${_type}/${name}.svg`;

  const svgClass = classNames("kuika-svg", className, {
    "kuika-svg-disabled": disabled
  });

  if (useOriginalColors) {
    // <img> width/height attributes need a concrete pixel number — unlike the mask-image
    // variant below, they can't fall back to the --kuika-svg-size custom property a
    // container's kuika-control-size mixin may have written.
    const resolvedWidth: number = width ?? 16;
    const resolvedHeight: number = height ?? 16;

    return (
      <img
        {...rest}
        src={imageSrc}
        width={resolvedWidth}
        height={resolvedHeight}
        className={classNames("kuika-svg-original", className, { "kuika-svg-disabled": disabled })}
        style={{
          ...style,
          width: `${resolvedWidth}px`,
          height: `${resolvedHeight}px`,
          cursor: disabled ? "not-allowed" : pointer ? "pointer" : "",
          opacity: disabled ? 0.4 : 1
        }}
        onClick={!disabled ? onClick : undefined}
        alt={name}
      />
    );
  }

  // Only set --kuika-svg-width/-height when the caller gave an explicit size. Left unset,
  // kuika-svg.scss falls back to --kuika-svg-size — the size a container's
  // kuika-control-size mixin writes — before its own 16px default.
  const computedStyle: React.CSSProperties = {
    ...style,
    ...(width !== undefined ? { "--kuika-svg-width": `${width}px` } : {}),
    ...(height !== undefined ? { "--kuika-svg-height": `${height}px` } : {}),
    "--kuika-svg-color": disabled ? "var(--dark-4)" : color,
    "--kuika-svg-mask": `url(${imageSrc})`,
    cursor: disabled ? "not-allowed" : pointer ? "pointer" : ""
  } as React.CSSProperties;

  const masked: React.ReactElement = (
    <div {...rest} className={svgClass} style={computedStyle} onClick={!disabled ? onClick : undefined} />
  );

  if (draw) {
    return <KuikaDrawnSvg imageSrc={imageSrc} className={svgClass} style={computedStyle} fallback={masked} />;
  }

  return masked;
};

export default KuikaSvg;
